import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Settings2 } from "lucide-react";
import { ScoreCard, Breakdown, ReviewList } from "@/components/ResultView";
import { Mascot } from "@/components/Mascot";
import { usePrefs } from "@/context/PrefsContext";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

function LevelLadder({ levels, rec, passPct, rules }) {
  const { t } = usePrefs();
  const ruleText = rules ? Object.entries(rules).map(([k, v]) => `${k} ${v}%`).join(" · ") : null;
  return (
    <div className="soft-card p-6 sm:p-8" data-testid="level-ladder">
      <h3 className="text-base font-semibold">{t("levelAnalysis")}</h3>
      <p className="mt-1 text-xs text-muted-foreground">{ruleText ? t("levelAnalysisDescRules").replace("{rules}", ruleText) : t("levelAnalysisDesc").replace("{pct}", passPct)}</p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {levels.map((l) => {
          const need = l.pass_pct ?? passPct;
          const ok = l.passed ?? l.pct >= need;
          const reqs = l.requirements || [];
          return (
            <div key={l.level} data-testid={`level-${l.level}`} data-passed={ok} className={`rounded-xl border p-4 ${l.level === rec ? "border-primary ring-1 ring-primary" : ok ? "border-sage/40 bg-sage-soft/40" : "border-border"}`}>
              <div className="flex items-center justify-between"><span className="font-jpserif text-2xl">{l.level}</span><span className={`text-xs font-semibold ${ok ? "text-sage" : "text-primary"}`}>{ok ? t("passed") : t("notPassed")}</span></div>
              <p className="mt-2 text-sm text-muted-foreground">{l.correct}/{l.total} · <b className={l.pct >= need ? "text-foreground" : "text-primary"}>{l.pct}%</b> <span className="text-[11px]">(≥ {need}%)</span></p>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className={`h-full rounded-full ${ok ? "bg-sage" : "bg-primary/70"}`} style={{ width: `${l.pct}%` }} /></div>
              {reqs.length > 0 && <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid={`level-${l.level}-reqs`}>{t("prereq")}: {reqs.map((r, i) => <span key={r.level}>{i > 0 && ", "}<b className={r.ok ? "text-sage" : "text-primary"}>{r.level} ≥ {r.need}%</b></span>)}</p>}
              {l.fail_reason === "prereq" && l.failed_req && <p className="mt-1 text-[11px] text-primary">{t("failPrereq").replace("{prev}", l.failed_req.level).replace("{need}", l.failed_req.need).replace("{pct}", l.failed_req.pct ?? "–")}</p>}
              {l.level === rec && <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-primary" data-testid="level-recommended-tag">{t("startHere")}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AssessmentResult() {
  const { t, area } = usePrefs();
  const { user, setUser } = useAuth();
  const { state } = useLocation();
  const [res, setRes] = useState(state?.result || null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (res) return;
    api.get("/assessment/latest").then((r) => setRes(r.data)).catch(() => setMissing(true));
  }, [res]);

  if (missing) return <div className="text-sm text-muted-foreground" data-testid="assessment-result-empty">{t("noAssessment")} <Link to="/assessment" className="underline">{t("startAssessment")}</Link></div>;
  if (!res) return <p className="text-sm text-muted-foreground">{t("loading")}</p>;

  const setTarget = async () => {
    try {
      const { data } = await api.put("/profile/target", { target_level: res.recommended_level });
      setUser(data); toast.success(t("saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const weak = res.weak_areas || [];

  return (
    <div className="mx-auto max-w-2xl space-y-5" data-testid="assessment-result-page">
      {res.mascot && <div className="soft-card p-5 fade-up"><Mascot m={res.mascot} size={120} testid="result-mascot" /></div>}
      <ScoreCard result={res} title={t("resultTitle")}>
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-muted/60 px-4 py-3">
          <div className="text-sm">{t("recommendedLevel")}: <span data-testid="recommended-level" className="font-jpserif text-2xl">{res.recommended_level}</span>{res.ready_next && <span className="ml-2 rounded-full bg-sage-soft px-2 py-0.5 text-xs font-semibold text-sage" data-testid="ready-next-badge">{t("readyNext")}</span>}</div>
          <div className="flex flex-wrap gap-2">
            {user?.target_level !== res.recommended_level && <button data-testid="set-target-button" onClick={setTarget} className="rounded-full bg-foreground px-4 py-2 text-xs font-semibold text-background">{t("setTarget")} {res.recommended_level}</button>}
            <Link data-testid="result-settings-link" to="/settings" className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-semibold hover:bg-accent"><Settings2 className="h-3.5 w-3.5" />{t("setTargetSettings")}</Link>
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground" data-testid="result-next-hint">{t("resultNextHint").replace("{level}", res.recommended_level)}</p>
      </ScoreCard>
      {res.levels?.length > 0 && <LevelLadder levels={res.levels} rec={res.recommended_level} passPct={res.pass_pct || 60} rules={res.pass_rules} />}
      <Breakdown breakdown={res.breakdown} />
      <div className="soft-card p-6 sm:p-8" data-testid="weak-areas-card">
        <h3 className="text-base font-semibold">{weak.length ? t("weakAreas") : t("strong")}</h3>
        <div className="mt-3 flex flex-wrap gap-2">{(weak.length ? weak : res.breakdown.map((b) => b.area)).map((a) => <span key={a} className={`rounded-full px-3 py-1 text-xs font-medium ${weak.length ? "bg-primary/10 text-primary" : "bg-sage-soft text-sage"}`}>{area(a)}</span>)}</div>
        <p className="mt-4 text-xs uppercase tracking-wide text-muted-foreground">{t("nextStep")}</p>
        <div className="mt-3 flex flex-wrap gap-3">
          <Link data-testid="result-go-dashboard" to="/dashboard" className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">{t("continueLearn")}</Link>
          <Link data-testid="result-go-modules" to="/modules" className="rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:bg-accent">{t("openModules")}</Link>
        </div>
      </div>
      <ReviewList items={res.items} />
    </div>
  );
}
